import {
    countActiveCells,
    generateSymbolGrid,
    type DirectionalBiasMode,
    type BinaryGrid,
    type SymbolSymmetry,
} from "./symbolGrid";
export type { DirectionalBiasMode } from "./symbolGrid";
import {
    applyStrokeGrammar,
    type SymbolGrammarMode,
    type SymbolShapeType,
} from "./symbolGrammar";
export type { SymbolGrammarMode } from "./symbolGrammar";
export type { SymbolShapeType } from "./symbolGrammar";
import {
    buildSymbolDotPoints,
    buildSymbolPathD,
    buildSymbolPathDs,
    type Point,
    type SymbolLayoutOptions,
} from "./symbolStroke";

export type SymbolStyleProfile = "neutral" | "bengali-like" | "cjk-like";
export type SymbolPathMode = "single" | "multi";

export interface SymbolGeneratorParams {
    width: number;
    height: number;
    rows: number;
    cols: number;
    density: number;
    complexity: number;
    branching: number;
    mergeBias: number;
    directionalBiasMode: DirectionalBiasMode;
    directionalBiasStrength: number;
    maxPathLength: number;
    pathGapCells: number;
    symmetry: SymbolSymmetry;
    styleProfile: SymbolStyleProfile;
    grammarMode: SymbolGrammarMode;
    dotDensity: number;
    shapeFrequency: number;
    shapeType: SymbolShapeType;
    curvature: number;
    cornerStyle: "sharp" | "rounded" | "bevel";
    cornerAmount: number;
    strokeWidth: number;
    pathMode: SymbolPathMode;
    gridDisplacementStrength?: number;
    seed: string;
}

export interface SymbolGenerationResult {
    grid: BinaryGrid;
    pathD: string;
    pathDs: string[];
    dotPoints: Point[];
    activeCells: number;
    fillRatio: number;
    hash: string;
}

export function generateAbstractSymbol(
    params: SymbolGeneratorParams,
): SymbolGenerationResult {
    const layoutOptions: SymbolLayoutOptions = {
        rowWeights: buildNoisyWeights(params.rows, `${params.seed}|rows`),
        colWeights: buildNoisyWeights(params.cols, `${params.seed}|cols`),
        displacementStrength: Math.max(
            0,
            Math.min(1, params.gridDisplacementStrength ?? 1),
        ),
    };

    const rng = seededRng(params.seed);
    const grid = generateSymbolGrid(
        {
            rows: params.rows,
            cols: params.cols,
            density: params.density,
            complexity: params.complexity,
            branching: params.branching,
            mergeBias: params.mergeBias,
            directionalBiasMode: params.directionalBiasMode,
            directionalBiasStrength: params.directionalBiasStrength,
            maxPathLength: params.maxPathLength,
            pathGapCells: params.pathGapCells,
            symmetry: params.symmetry,
            keepLargestComponent: params.pathMode === "single",
        },
        rng,
    );
    applyStrokeGrammar(
        grid,
        rng,
        params.grammarMode,
        params.complexity,
        params.dotDensity,
        params.shapeFrequency,
        params.shapeType,
    );
    const activeCells = countActiveCells(grid);
    const totalCells = Math.max(1, params.rows * params.cols);
    const fillRatio = activeCells / totalCells;

    const pathD = buildSymbolPathD(
        grid,
        params.width,
        params.height,
        {
            styleProfile: params.styleProfile,
            curvature: params.curvature,
            cornerStyle: params.cornerStyle,
            cornerAmount: params.cornerAmount,
        },
        params.maxPathLength,
        params.pathGapCells,
        layoutOptions,
    );
    const pathDs =
        params.pathMode === "multi"
            ? buildSymbolPathDs(
                  grid,
                  params.width,
                  params.height,
                  {
                      styleProfile: params.styleProfile,
                      curvature: params.curvature,
                      cornerStyle: params.cornerStyle,
                      cornerAmount: params.cornerAmount,
                  },
                  params.maxPathLength,
                  params.pathGapCells,
                  layoutOptions,
              )
            : pathD
              ? [pathD]
              : [];
    const dotPoints = buildSymbolDotPoints(
        grid,
        params.width,
        params.height,
        layoutOptions,
    );

    return {
        grid,
        pathD,
        pathDs,
        dotPoints,
        activeCells,
        fillRatio,
        hash: hashString(
            `${params.seed}|${pathDs.join("|")}|${dotPoints.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(";")}`,
        ),
    };
}

function buildNoisyWeights(count: number, seed: string): number[] {
    const n = Math.max(1, Math.floor(count));
    const rng = seededRng(seed);
    const base = Array.from({ length: n }, () => 0.15 + rng() * 2.4);
    if (n <= 2) return base;
    const smooth: number[] = [];
    for (let i = 0; i < n; i += 1) {
        const prev = base[(i - 1 + n) % n] ?? base[i]!;
        const curr = base[i]!;
        const next = base[(i + 1) % n] ?? base[i]!;
        smooth.push(prev * 0.1 + curr * 0.8 + next * 0.1);
    }
    return smooth;
}

export function isValidGeneration(result: SymbolGenerationResult): boolean {
    if (!result.pathD && result.dotPoints.length === 0) return false;
    if (result.fillRatio < 0.06) return false;
    if (result.fillRatio > 0.72) return false;
    return true;
}

export function seededRng(seed: string): () => number {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i += 1) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return () => {
        h += h << 13;
        h ^= h >>> 7;
        h += h << 3;
        h ^= h >>> 17;
        h += h << 5;
        return ((h >>> 0) % 4294967296) / 4294967296;
    };
}

function hashString(input: string): string {
    let hash = 5381;
    for (let i = 0; i < input.length; i += 1) {
        hash = (hash * 33) ^ input.charCodeAt(i);
    }
    return (hash >>> 0).toString(36);
}
